import React, { useEffect, useState, useRef, useCallback } from 'react'
import Loading from '../components/Loading'
import { useLocation } from 'react-router-dom'
import { PuffLoader } from 'react-spinners'
import { db } from '../..'
import { ref, onValue, set, push } from 'firebase/database'
import { useNavigate } from 'react-router-dom'
import BottomMenu from './components/BottomMenu'
import ConfirmModal from '../components/ConfirmModal'

// 2つの座標（緯度・経度）から距離を計算
const calcDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6378137; // 地球の半径
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const d = R * c;
    return d;
};

const Catch = ({userData}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { gameId, teamId, gameMode } = location.state;
    const [myLocation, setMyLocation] = useState({lat: 0, lng: 0});
    const [allLocations, setAllLocations] = useState([]);
    const [teams, setTeams] = useState([]);
    const [uploadDone, setUploadDone] = useState(false);
    const [posError, setPosError] = useState("");
    const [selected, setSelected] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false); // 処理が完了したかどうか
    const [prevRole, setPrevRole] = useState(); // 追加: 前回の自チームrole
    const [confirmModal, setConfirmModal] = useState(null); // 相手チーム確認モーダル（{ title, message, onConfirm }）
    const myLocationRef = useRef(myLocation); // onValueコールバック内から最新の位置情報を参照するためのref
    const rawLocationsRef = useRef(null); // changeRoleLocationsの生スナップショットを保持し、自分の位置確定後にも再計算できるようにする

    // タイムアウト処理
    const handleTimeout = useCallback(() => {
        alert("相手のチームが見つかりませんでした。");
        navigate(-1);
    }, [navigate]);

    // 位置情報のアップロード
    const uploadLocation = useCallback((pos) => {
        set(ref(db, 'gameLogs/' + gameId + '/changeRoleLocations/' + teamId), pos)
        .then(() => {
            setUploadDone(true);
        })
        .catch((error) => {
            console.error('位置情報のアップロードに失敗しました:', error);
            alert('位置情報のアップロードに失敗しました。もう一度お試しください。');
        });
    }, [gameId, teamId]);

    // changeRoleLocationsの生データと自分の位置情報から、近くのチーム一覧を計算する
    const processLocations = useCallback(() => {
        if (!myLocationRef.current.time) return;

        const rawLocations = rawLocationsRef.current;
        if (!rawLocations) {
            setAllLocations([]);
            return;
        }

        let locations = { ...rawLocations };

        // 他チームの位置情報を取得
        Object.keys(locations).forEach((teamIndex) => {
            // 自チームのデータは無視
            if(Number(teamIndex) === Number(teamId)) {
                delete locations[teamIndex];
                return;
            };

            // 1分以上前のデータは無視
            if((new Date(myLocationRef.current.time)).getTime() - (new Date(locations[teamIndex].time)).getTime() > 60000) {
                delete locations[teamIndex];
                return;
            };

            const location = locations[teamIndex];

            // 位置情報の距離計算
            const distance = calcDistance(myLocationRef.current.lat, myLocationRef.current.lng, location.lat, location.lng);

            locations[teamIndex] = { ...location, distance, teamIndex };
        });

        setAllLocations(Object.values(locations).sort((a, b) => a.distance - b.distance));
    }, [teamId]);

    // myLocationが更新されるたびにrefへ反映し、候補一覧を再計算する
    // （自分の位置情報取得が完了するより前にchangeRoleLocationsのスナップショットが届いた場合でも、
    //   ここで再計算することで候補が表示されないままになるのを防ぐ）
    useEffect(() => {
        myLocationRef.current = myLocation;
        processLocations();
    }, [myLocation, processLocations]);

    // チーム情報の取得（gameIdが変わらない限り再購読しない）
    useEffect(() => {
        const teamsRef = ref(db, 'gameLogs/' + gameId + '/teams');
        const unsubscribeTeams = onValue(teamsRef, (snapshot) => {
            const teamsTmp = snapshot.val();
            setTeams(teamsTmp);
        });

        return () => unsubscribeTeams();
    }, [gameId]);

    // 自チームの位置情報の取得（初回マウント時のみ）
    useEffect(() => {
        navigator.geolocation.getCurrentPosition((position) => {
            const pos = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                time: new Date().toISOString()
            };

            // 位置情報のアップロード
            uploadLocation(pos);
            // 現在地のセット
            setMyLocation(pos);
        }, (error) => {
            setPosError(error.message);
        });
    }, [uploadLocation]);

    // 追加: 自チームのrole変化を監視し、変化したら前画面に戻る
    useEffect(() => {
        if (!teams || !teams[teamId]) return;
        if (prevRole === undefined) {
            setPrevRole(teams[teamId].role);
            return;
        }
        if (teams[teamId].role !== prevRole) {
            navigate(-1);
        }
    }, [teams, teamId, prevRole, navigate]);


    useEffect(() => {
      // 60秒後にタイムアウト処理を実行
      const timeoutId = setTimeout(() => {
        if (!isCompleted) {
          handleTimeout();
        }
      }, 60000); // 60秒

      return () => clearTimeout(timeoutId); // クリーンアップ
    }, [isCompleted, handleTimeout]);

    // 近くのチームを探す
    // 依存はgameIdのみとし、geolocationの更新（myLocation変化）のたびに購読しなおさないようにする。
    // 最新のmyLocationはmyLocationRef経由でコールバック内から参照する。
    useEffect(() => {
        const gameRef = ref(db, 'gameLogs/' + gameId + '/changeRoleLocations');
        const unsubscribe = onValue(gameRef, (snapshot) => {
            // 自分の位置情報がまだ確定していなくても生データは保持しておく。
            // （確定前にスナップショットが届くと候補が計算されないまま取り残されるため、
            //   myLocation確定時にprocessLocationsで再計算する）
            rawLocationsRef.current = snapshot.val();
            processLocations();
        });

        return () => unsubscribe();
    }, [gameId, processLocations]);

    //役交代の承諾
    const acceptChangeRole = (teamIndex) => {
        setConfirmModal({
            title: '相手チームの確認',
            message: '相手チームは ' + teams[teamIndex].name + ' ですか？',
            onConfirm: () => sendChangeRoleRequest(teamIndex)
        });
    }

    // 役交代リクエストの送信
    const sendChangeRoleRequest = (teamIndex) => {
        setSelected(true);

        const changeRoleRef = ref(db, 'roleChangeRequest');
        push(changeRoleRef, {
            gameId: gameId,
            teamId: Number(teamId),
            opponentTeamId: Number(teamIndex),
            date: new Date().toISOString(),
            mode: gameMode, // 0: 代わり鬼, 1: 増やし鬼
            position: myLocation
        }).then(() => {
            setIsCompleted(true);

            // setTimeout(() => {
            //     navigate(-1);
            // }, 3000);
        }).catch((error) => {
            console.error('役交代リクエストの送信に失敗しました:', error);
            alert('役交代リクエストの送信に失敗しました。もう一度お試しください。');
            setSelected(false);
        });
    }

    return (
        <div className='max-w-xl mx-auto my-6'>
            <Loading text="ミッションを読込み中…" />

            <ConfirmModal
                isOpen={confirmModal !== null}
                mode="confirm"
                title={confirmModal?.title}
                message={confirmModal?.message}
                onConfirm={() => {
                    const onConfirm = confirmModal?.onConfirm;
                    setConfirmModal(null);
                    onConfirm && onConfirm();
                }}
                onCancel={() => setConfirmModal(null)}
            />
            <h1 className='text-2xl mb-6 mx-4 text-center'>つかまえた / つかまった</h1>

            <div className='my-8 max-w-44 mx-auto'>
                <PuffLoader color='#8b5cf6' loading={true} size={178} />
            </div>

            <div className='text-center animate-pulse text-gray-600 mb-10'>
                {uploadDone ? (selected ? '相手の承諾を待っています…' : '近くのチームをさがしています…') : '位置情報をアップロード中…'}
                {posError && (<div className='text-red-600'>{posError}</div>)}
            </div>

            {/* 他チームの位置情報（近い順） */}
            <div className={'mb-8 mx-4 ' + (selected ? 'hidden' : '')}>
                {
                    allLocations && Object.keys(allLocations).filter(index => allLocations[index].teamIndex !== teamId).map((index) => (
                        <div key={"team_" + index}>
                        {
                            teams[allLocations[index].teamIndex].role !== teams[teamId].role && (
                                <button 
                                    key={index} 
                                    className='w-full rounded-lg bg-white px-3 py-4 mb-4 shadow-sm relative cursor-pointer hover:bg-purple-50 border' 
                                    onClick={() => acceptChangeRole(Number(allLocations[index].teamIndex))}
                                    disabled={selected}
                                >
                                    <div className='text-lg'><i className='bi bi-people-fill mx-2 text-xl' style={{color: teams[allLocations[index].teamIndex].color}}></i>{teams[allLocations[index].teamIndex].name}</div>
                                    <div className='absolute end-3 top-3 text-purple-700 text-lg'>{allLocations[index].distance.toFixed(1)} m</div>
                                </button>
                            )
                        }
                        </div>
                    ))
                }
            </div>

            {/* 役交代が選択された場合のメッセージ */}
            {selected && (
                <div className='text-center text-gray-600 mb-4'>
                    <i className='bi bi-check-lg text-lime-500 me-1'></i>リクエスト送信済み
                </div>
            )}

            {/* 下部固定メニュー */}
            <BottomMenu gameId={gameId} teamId={teamId} />
        </div>
    )
}

export default Catch